import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
import "reflect-metadata";

/**
 * Collection of methods that are useful if you use the @Interfaces decorator on classes that implement interfaces.
 */
export class InterfaceUtils {
  /**
   * Determines if a class instance implements a particular interface.
   * Note that this assumes you have decorated your class with the @Interfaces decorator.
   * See exported Interfaces below or the Dev Guide for more information.
   * @param instance Class instance to test
   * @param iface Name (string) of the interface
   * @returns {boolean} true if the instance implements the interface
   */
  static is(instance, iface) {
    if (instance == null) {
      return false;
    }
    const type = ObjectUtils.getClass(instance);
    return InterfaceUtils.implements(type, iface);
  }

  /**
   * Soft cast of a class instance to a particular interface.
   * Note that this assumes you have decorated your class with the @Interfaces decorator.
   * See exported Interfaces below or the Dev Guide for more information.
   * Usage is like:
   * let spaceship: SpaceShip = new SpaceShip()
   * let raygun: IHaveRayguns = InterfaceUtils.as(spaceship, "IHaveRayguns")
   * Then use raygun through the IHaveRayguns interface.
   * @param instance Class instance to test
   * @param iface Name (string) of the interface
   * @returns {null} instance if the instance implements the interface,
   * null if the instance does not implement the interface.
   */
  static as(instance, iface) {
    return InterfaceUtils.is(instance, iface) ? instance : null;
  }

  /**
   * Determines if a type implements a particular interface.
   * @param type Class type or constructor (note that the class must mark
   * its implemented interfaces using the @Interfaces decorator.
   * @param iface Interface name
   * @returns {boolean}
   */
  static implements(type, iface) {
    while (type) {
      if (InterfaceUtils.implementsHelper(Reflect.getMetadata("Interfaces", type), iface)) {
        return true; // we have a winner
      }

      // Continue up proto chain
      type = ClassUtils.getBaseClass(type);
    }
    return false;
  }
  static implementsHelper(typeNames, iface) {
    if (typeNames != null) {
      for (const typeName of typeNames)
      // depth-first...
      {
        if (iface === typeName || InterfaceUtils.implementsHelper(InterfaceUtils.extensionMap[typeName], iface))
          // recurse
          {
            return true; // we have a winner
          }
      }
    }
    return false; // no dice
  }

  /**
   * Declare that an interface extends one or more other interfaces.
   * As an example, for this 'extending' interface:
   * <code>
   * export interface IOpCommonStatus extends IOpItem, IOpCommonIssues
   * </code>
   * Call this:
   * <code>
   * InterfaceUtils.interfaceExtends("IOpCommonStatus", "IOpItem", "IOpCommonIssues");
   * </code>
   * IMPORTANT: Experience has taught us that these calls are best gathered into a centralized 'registry'.
   * Henceforth, calls for AbWeb extending interfaces are in the AbWebServicesInitializer.interfaceExtends method.
   * NOTE: It is the responsibility of the extending interface publisher to add a call to the right place!
   * @param iface the interface that is extending other interfaces
   * @param first the first interface being extended
   * @param others add'l interfaces being extended
   */
  static interfaceExtends(iface, first, ...others) {
    InterfaceUtils.extensionMap[iface] = [first].concat(others);
  }
}

/**
 * This defines the @Interfaces annotation that can be used to decorate classes that implement interfaces.
 * Simply add this decorator to any class that implements one or more interfaces. Then add the list of interface
 * names as the decorator arguments.
 * Although this is optional, using this decorator allows you to use the InterfaceUtils methods to test for
 * interfaces and to do soft casting similar to the ActionScript "is" and "as" operators. See InterfaceUtils.ts
 * and the Dev Guide for more details.
 * @param value Array of strings representing the names of the interfaces implemented by this class.
 * e.g., @Interfaces(["IHaveWheels", "IHaveLeather"])
 */
_defineProperty(InterfaceUtils, "extensionMap", {});
export function Interfaces(value) {
  return function (target) {
    Reflect.defineMetadata("Interfaces", value, target);
  };
}
//# sourceMappingURL=InterfaceUtils.js.map
